import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Constants } from '../../../constants/constants';
import { DAXQueryDto } from '../../../dto/powerbi/dax.query.dto';
import { PowerbiService } from '../../../services/powerbi.service';
import { DownloadDB } from '../../../dto/observatorio/download-db.dto';
import { AlertaModalService } from '../../../services/util/alerta-modal.service';
import { RequestFiltersContarDatos } from '../../../dto/powerbi/request.filters.contardatos.dto';
import { ModalEmailComponent } from '../modal-email/modal-email.component';
import { query } from 'express';
import { DescargarBaseDatos } from '../../../dto/powerbi/descargar-base-datos.dto';
import { ModalDownloadComponent } from '../modal-download/modal-download.component';
import { SlicerDto } from '../../../dto/powerbi/slicer.dto';
import { ReportComponent } from '../report/report.component';
import { PortalDatosComponent } from '../portal-datos/portal-datos.component';

@Component({
  selector: 'app-download-db',
  templateUrl: './download-db.component.html',
  styleUrl: './download-db.component.scss',
})
export class DownloadDBComponent implements OnInit {
  // Define la lista de descargas.
  downloads: DownloadDB[] = [];
  // Define bandera de super administrador.
  esSuperAdmin = false;
  // Define el email del usuario.
  userEmail: string | null = null;
  // Define la clave actual.
  currentKey = '';
  // Define el estado de autenticación.
  isAuth = false;
  nameFile = '';

  @ViewChild(ModalEmailComponent)
  modalEmail!: ModalEmailComponent;

  @ViewChild(ModalDownloadComponent)
  modalDown!: ModalDownloadComponent;

  @Input() reportMetaDB!: string;
  @Input() reportRef!: ReportComponent;
  @Input() portalRef!: PortalDatosComponent;

  constructor(
    private pbiService: PowerbiService,
    private alertaModalService: AlertaModalService
  ) {}

  // Define ngOnInit
  ngOnInit(): void {
    const infoInicioSesion = JSON.parse(
      sessionStorage.getItem(Constants.USER_TYPE) ?? '{}'
    );
    this.esSuperAdmin = infoInicioSesion.Nombre === 'Superadministrador';
    let query = `EVALUATE FILTER('metadata OBJ_Met_Rep_Pag','metadata OBJ_Met_Rep_Pag'[Tablero] = "${this.reportMetaDB}")`;
    let dto: DAXQueryDto = {
      datasetId: 'pag',
      query: query,
    };
    this.pbiService.getViewBag(dto).subscribe(response => {
      response.forEach((element: any) => {
        let download: DownloadDB = {
          report: element['metadata OBJ_Met_Rep_Pag[Tablero]'],
          key: element['metadata OBJ_Met_Rep_Pag[Identificador]'],
          name: element['metadata OBJ_Met_Rep_Pag[Nombre]'],
          description: element['metadata OBJ_Met_Rep_Pag[Descripcion]'],
        };
        this.downloads.push(download);
      });
    });
  }

  abrirModalEmail() {
    setTimeout(() => {
      this.modalEmail.abrirCerrarModal();
    }, 500);
  }

  abrirModalDown() {
    setTimeout(() => {
      this.modalDown.abrirCerrarModal();
    }, 500);
  }

  registrarEmail(dto: any) {
    this.userEmail = dto.correo;
    this.exportDBParaCorreo(this.currentKey, dto.userId, this.userEmail);
  }

  // Método específico para exportar DB cuando se envía a correo
  async exportDBParaCorreo(key: string, userId: string | null, email: string | null) {
    const query: DescargarBaseDatos = {
      report_id: key,
      usuario_id: userId ? userId : undefined,
      usr_mail: email ? email : undefined,
      slicers: this.reportRef ? await this.reportRef.getFilters() : await this.portalRef.getFilters()
    };
    
    this.pbiService.exportDB(query).subscribe(
      response => {
        // Para envío a correo, siempre mostrar mensaje de confirmación
        this.alertaModalService.exito(
          'Solicitud completada exitosamente',
          'La solicitud para descargar la base de datos se ha completado exitosamente. En un plazo de 10 minutos, recibirá un mensaje en la bandeja de correo electrónico proporcionada, el cual incluirá el enlace para descargar el documento.'
        );
      },
      error => {
        this.alertaModalService.error('Error al procesar la solicitud de envío por correo');
      }
    );
  }

  downloadFile(key: string, name: string) {
    this.nameFile = name;
    const userData = JSON.parse(
      sessionStorage.getItem(Constants.USER_INFORMATION_STORAGE) ?? '{}'
    );
    const userId = userData.idUsuario ?? null;
    const userIdString = userId ? String(userId) : null;
    this.currentKey = key;
    
    // Si el usuario está autenticado, proceder directamente
    if (userId != null) {
      this.exportDB(key, userIdString, null);
      return;
    }
    
    // Si no está autenticado, mostrar opciones
    this.mostrarOpcionesDescarga();
  }

  // Método para mostrar las opciones de descarga cuando el usuario no está autenticado
  mostrarOpcionesDescarga() {
    // Mostrar modal con texto personalizado para seleccionar tipo de descarga
    this.alertaModalService.confirmacion(
      '¿Cómo desea recibir el reporte?',
      'Seleccione una de las siguientes opciones para recibir su reporte',
      () => {
        // Opción: Confirmar = Enviar a Correo
        this.abrirModalEmail();
      },
      () => {
        // Opción: Cancelar = Descarga en Línea
        this.descargarEnLinea();
      }
    );
  }

  // Método para manejar la descarga en línea
  descargarEnLinea() {
    this.exportDB(this.currentKey, null, null);
  }

  async descargar() {
    this.pbiService.downloadFile(this.currentKey).subscribe(
      (response: Blob) => {
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = this.nameFile + '.xlsx';
        document.body.appendChild(anchor);
        anchor.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(anchor);
        if (response) {
          this.alertaModalService.exito(
            '¡Éxito!',
            'El archivo se ha descargado correctamente.'
          );
        }
      },
      error => {
        this.alertaModalService.error('Error al descargar el archivo');
      }
    );
  }

  async exportDB(key: string, userId: string | null, email: string | null) {
    this.isAuth = email ? false : true;
    const query: DescargarBaseDatos = {
      report_id: key,
      usuario_id: userId ? userId : undefined,
      usr_mail: email ? email : undefined,
      slicers: this.reportRef ? await this.reportRef.getFilters() : await this.portalRef.getFilters()
    };
    
    this.pbiService.exportDB(query).subscribe(
      response => {
        if (
          response.status === 'success' &&
          !response.message.includes('generando')
        ) {
          this.descargar();
        } else {
          // Para descarga en línea sin autenticación, mostrar modal específico
          if (!email && !userId) {
            this.abrirModalDown();
          } else {
            this.abrirModalDown();
          }
        }
      },
      error => {
        this.alertaModalService.error('Error al descargar el archivo');
      }
    );
  }

  // Método para verificar si el usuario está autenticado
  isUserAuthenticated(): boolean {
    const userData = JSON.parse(
      sessionStorage.getItem(Constants.USER_INFORMATION_STORAGE) ?? '{}'
    );
    return userData.idUsuario != null;
  }

  // Método para manejar envío a correo directamente
  enviarACorreo(key: string, name: string) {
    this.nameFile = name;
    this.currentKey = key;
    this.abrirModalEmail();
  }

  // Método para manejar descarga en línea directamente
  descargaEnLinea(key: string, name: string) {
    this.nameFile = name;
    this.currentKey = key;
    this.descargarEnLinea();
  }
}
